/** @jsx isolateComponent */

import { isolateComponent } from '@zapier/common-utils';

import { SkeletonBlock } from '@zapier/design-system';

import { useSendFullStoryEvent, useTrackEvent } from '@zapier/toolbox-tracking';

import {
  Interval,
  PlanType,
  TrialExtensionReason,
  trialExtensionReasons,
} from '@zapier/design-system-revenue/src/types';

import { useCurrentAccount, usePlanStack } from './hooks';

import { BackButton } from './BackButton';
import { getUpgradeReasonContent } from './getUpgradeReasonContent';
import { TaskLimitUpgradeNudge } from './TaskLimitUpgradeNudge';
import { TaskLimit80PercentUpgradeNudge } from './TaskLimit80PercentUpgradeNudge';
import { TrialExtensionPaymentMethodModalTrigger } from './TrialExtensionPaymentMethodModalTrigger';
import { UpgradeButton } from './UpgradeButton';
import { UpgradeNudgeLayout } from './UpgradeNudgeLayout';

type Props = {
  appName?: string;
  onBack?: () => void;
  onClose: () => void;
  onExtendTrial: () => void;
  onUpgradeClick: () => void;
  plan: {
    amount: number;
    interval: Interval;
    name: string;
    tasks: number;
    type: PlanType;
  };
  reason: TrialExtensionReason;
};

export const UpgradeReason = ({
  appName,
  onBack,
  onClose,
  onExtendTrial,
  onUpgradeClick,
  plan: nextPlan,
  reason = trialExtensionReasons.unknown as TrialExtensionReason,
}: Props) => {
  const eventProperties = {
    upgradeReason: reason,
  };

  useSendFullStoryEvent('Viewed Upgrade Modal', eventProperties);
  useTrackEvent('revenue: viewed-upgrade-modal', eventProperties);

  const {
    activeTrialType,
    currentPlan,
    enabledTrialExtensions,
    isLoading: isLoadingCurrentAccount,
    tasksRemaining,
  } = useCurrentAccount();

  const { planStack, isLoading: isLoadingPlanStack } = usePlanStack({
    planType: activeTrialType as PlanType,
    skip: activeTrialType !== 'Team' && activeTrialType !== 'Company',
  });

  const lowestTeamOrCompanyPlan = planStack ? planStack.plans[0] : undefined;
  const isLoading = isLoadingCurrentAccount || isLoadingPlanStack;

  const canExtendTrial =
    !activeTrialType &&
    currentPlan?.amount === 0 &&
    !(enabledTrialExtensions ?? []).includes(reason);

  const handleBack = () => (onBack ? onBack() : () => {});

  const content = getUpgradeReasonContent({
    appName,
    canExtendTrial,
  })[reason];

  const extendTrialButton = (
    <TrialExtensionPaymentMethodModalTrigger
      currentPlanName={currentPlan?.name ?? ''}
      key="extend-trial"
      onBack={onBack}
      onClick={onClose}
      onExtendTrial={onExtendTrial}
      trialPlanAmount={nextPlan.amount}
      trialPlanInterval={nextPlan.interval}
      trialPlanName={nextPlan.name}
    />
  );

  const secondaryCta = canExtendTrial ? (
    extendTrialButton
  ) : (
    <BackButton onClick={handleBack} />
  );

  if (isLoading || !currentPlan) {
    return <SkeletonBlock height={335} width="100%" />;
  }

  /**
   * TODO: Eventually, we should refactor the other nudges to be set up
   * like the TaskLimitUpgradeNudge is.
   */
  switch (reason) {
    case trialExtensionReasons.taskLimit:
      return (
        <TaskLimitUpgradeNudge
          activeTrialType={activeTrialType}
          currentPlan={currentPlan}
          lowestTeamOrCompanyPlan={lowestTeamOrCompanyPlan}
          nextPlan={nextPlan}
          onClose={onClose}
        />
      );

    case trialExtensionReasons.taskLimit80Percent:
      return (
        <TaskLimit80PercentUpgradeNudge
          activeTrialType={activeTrialType}
          currentPlan={currentPlan}
          lowestTeamOrCompanyPlan={lowestTeamOrCompanyPlan}
          nextPlan={nextPlan}
          onClose={onClose}
          tasksRemaining={tasksRemaining}
        />
      );
  }

  return (
    <UpgradeNudgeLayout
      {...content}
      buttons={[<UpgradeButton onClick={onUpgradeClick} />, secondaryCta]}
      subtitleText={content.subtitleText}
    />
  );
};
